import '../../tixologist/ImgContentSection/ImgContentSection.css';
import 'features/common/components/Punchup/composites/editors/PunchupInlineEditor/PunchupInlineEditor.css';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupButton, PunchupAddButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupImagePreview } from 'features/common/components/Punchup/primitives/display/PunchupImagePreview';
import { EventImageForm } from '../../tixologist/ImgContentSection/EventImageForm/EventImageForm';
import { ImgContentSectionProps } from '../../tixologist/ImgContentSection/types';
import { CircleWithIcon } from 'features/common/components/CircleWithIcon';

export const ImgContentSection = ({ eventImgCt, eventImageUrl, media }: ImgContentSectionProps) => {
	const { t } = useTranslation();
	const [imgCt, setImgCt] = useState(eventImgCt);
	const [videoCt, setVideoCt] = useState(0);
	const [editing, setEditing] = useState(false);

	useEffect(() => {
		if (!media) return;
		// Seed from 0, not eventImgCt — the main event image is tracked via eventImgCt separately.
		const [imgs, vids] = media.reduce(
			([imgs, vids], { type }) => (type === 'image' ? [imgs + 1, vids] : [imgs, vids + 1]),
			[0, 0]
		);
		setImgCt(eventImgCt + imgs);
		setVideoCt(vids);
	}, [media, eventImgCt]);

	if (editing) {
		return <EventImageForm onDone={() => setEditing(false)} />;
	}

	const imageMedia = (media ?? []).filter((m) => m.type === 'image');
	const hasMedia = imgCt > 0 || videoCt > 0;

	// Resolve preview URL: main event image takes priority, then first gallery image.
	const previewUrl = eventImageUrl
		? `${eventImageUrl}?alt=media`
		: imageMedia[0]
		? `${imageMedia[0].url}?alt=media`
		: undefined;

	return (
		<>
			{hasMedia ? (
				<div className="imgContentPreviewArea">
					{/* Always render PunchupImagePreview when we know there's an image (imgCt > 0),
					    even if the URL is unavailable — it will show "No preview available" in that case. */}
					{(imgCt > 0 || imageMedia[0]) && (
						<PunchupImagePreview
							src={previewUrl}
							alt={t('dashboard-imgContent-event-image-alt', 'Event image')}
							maxHeight={280}
							className="imgContentPreviewWrapper"
						/>
					)}
					{videoCt > 0 && (
						<div className="imgContentVideoBadge">
							<CircleWithIcon height="36px" width="36px" iconName="image" />
							<span className="imgContentVideoLabel">{t('dashboard-imgContent-videos', { videoCt })}</span>
						</div>
					)}
				</div>
			) : (
				<p className="imgContentEmpty">{t('dashboard-imgContent-empty', 'No images or videos yet')}</p>
			)}
			<div className="entityViewActions">
				{hasMedia ? (
					<PunchupButton type="button" variant="outline" onClick={() => setEditing(true)}>
						{t('button-edit')}
					</PunchupButton>
				) : (
					<PunchupAddButton type="button" onClick={() => setEditing(true)}>
						{t('dashboard-add-green')}
					</PunchupAddButton>
				)}
			</div>
		</>
	);
};
